import { Component, inject } from "@angular/core";
import { MessageService } from "primeng/api";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { AuthService } from "src/app/auth/auth.service";
import { UsersService } from "src/app/mods/users/users.service";
import { CommonService } from "src/app/shared/common.service";

interface AvatarUploadResponse {
  user?: Record<string, any>;
  msg?: string;
}

@Component({
  standalone: false,
  selector: "app-home-user-avatar-dialog",
  templateUrl: "./home-user-avatar-dialog.component.html",
  styleUrls: ["./home-user-avatar-dialog.component.scss"],
  providers: [MessageService],
})
export class HomeUserAvatarDialogComponent {
  private usersService = inject(UsersService);
  private authService = inject(AuthService);
  private commonService = inject(CommonService);
  private messageService = inject(MessageService);
  private dialogRef = inject(DynamicDialogRef);
  private dialogConfig = inject(DynamicDialogConfig);

  readonly maxFileSize = 5 * 1024 * 1024;
  currentUser: { avatarUrl?: string | null } | null = 
    this.dialogConfig.data?.user || this.authService.getUserFromLocalStorage();
  previewUrl = this.currentUser?.avatarUrl || '../../../assets/icons/icon-sbest-512x512.png';
  selectedFile: File | null = null;
  saving = false;

  triggerFileInput(input: HTMLInputElement): void {
    input.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      this.commonService.showErrorMessage(
        "Selecciona un archivo de imagen válido",
        this.messageService,
      );
      input.value = "";
      return;
    }

    if (file.size > this.maxFileSize) {
      this.commonService.showErrorMessage(
        "La imagen no debe exceder 5 MB",
        this.messageService,
      );
      input.value = "";
      return;
    }

    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.previewUrl =
        typeof reader.result === "string" ? reader.result : this.previewUrl;
    };
    reader.readAsDataURL(file);
  }

  save(): void {
    if (!this.selectedFile) {
      this.commonService.showErrorMessage(
        "Selecciona una imagen antes de guardar",
        this.messageService,
      );
      return;
    }

    this.saving = true;
    this.usersService.uploadProfileAvatar(this.selectedFile).subscribe({
      next: (response: AvatarUploadResponse) => {
        const updatedUser = this.authService.updateStoredUser(
          response.user || {},
        );
        this.commonService.showSuccessMessage(
          response.msg || "Foto de perfil actualizada correctamente",
          this.messageService,
        );
        this.saving = false;
        this.dialogRef.close(updatedUser || response.user);
      },
      error: (err: unknown) => {
        this.commonService.showErrorMessage(
          typeof err === 'string' ? err : "No fue posible actualizar la foto de perfil",
          this.messageService,
        );
        this.saving = false;
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
